import { DateTime, Duration } from 'luxon';

/**
 * Checks whether two time ranges overlap (have any shared time).
 *
 * Two ranges are considered non-overlapping (excluded) when:
 *   a.finish <= b.start || b.finish <= a.start
 *
 * A null boundary means unbounded (negative infinity for start, positive
 * infinity for finish), so a null boundary never causes exclusion.
 *
 * The boundary checks below (`rangeA.finish && ...`) are truthiness checks, not `!= null`
 * checks: a boundary of epoch `0` is indistinguishable from a missing one and is read as
 * unbounded rather than as a real (if unusual) instant. This has no effect on the `DateTime`
 * boundaries this function is normally called with — every `DateTime` instance is truthy — and
 * is left as-is deliberately for numeric epoch-second callers (e.g.
 * `PropertyShow/helpers/dataCaching.js`, which screens its own inputs with `isValidTimeRange`
 * before calling this function, and documents the `0` disagreement in its own JSDoc). Fixing it
 * here would touch every consumer for a boundary value that does not occur in any of them.
 * @param {{ start: object|null, finish: object|null }} rangeA
 * @param {{ start: object|null, finish: object|null }} rangeB
 * @returns {boolean} true if the ranges overlap
 */
export const hasTimeRangeOverlap = (rangeA, rangeB) => {
  // If a.finish exists and b.start exists, check a.finish <= b.start
  if (rangeA.finish && rangeB.start && rangeA.finish <= rangeB.start) {
    return false;
  }

  // If b.finish exists and a.start exists, check b.finish <= a.start
  if (rangeB.finish && rangeA.start && rangeB.finish <= rangeA.start) {
    return false;
  }

  return true;
};

/**
 * Checks whether two time ranges are excluded (have no overlap).
 * This is the logical inverse of {@link hasTimeRangeOverlap}.
 * @param {{ start: object|null, finish: object|null }} rangeA
 * @param {{ start: object|null, finish: object|null }} rangeB
 * @returns {boolean} true if the ranges have no overlap
 */
export const isTimeRangeExcluded = (rangeA, rangeB) => !hasTimeRangeOverlap(rangeA, rangeB);

/**
 * Checks whether two epoch-seconds ranges overlap, treating a null boundary
 * as unbounded. Unlike {@link hasTimeRangeOverlap}'s truthy checks, a
 * boundary of `0` is still treated as bounded here: each boundary is
 * converted to a DateTime (always truthy) before delegating to
 * hasTimeRangeOverlap, so only `null`/`undefined` is treated as unbounded.
 * @param {{ start: number|null, finish: number|null }} rangeA - Epoch-seconds range.
 * @param {{ start: number|null, finish: number|null }} rangeB - Epoch-seconds range.
 * @returns {boolean} true if the ranges overlap
 */
export const hasTimestampRangeOverlap = (rangeA, rangeB) => {
  const toBoundary = (seconds) => (seconds == null ? seconds : DateTime.fromSeconds(seconds));
  return hasTimeRangeOverlap(
    { start: toBoundary(rangeA.start), finish: toBoundary(rangeA.finish) },
    { start: toBoundary(rangeB.start), finish: toBoundary(rangeB.finish) },
  );
};

/**
 * Parse an aggregation into a Luxon `Duration`.
 * @param {string} aggregation - ISO 8601 duration, e.g. 'PT30M', 'P1D'.
 * @returns {Duration|null} The duration, or null when it is missing, malformed, zero or negative.
 */
export const getAggregationDuration = (aggregation) => {
  const duration = Duration.fromISO(aggregation);
  return duration.isValid && duration.toMillis() > 0 ? duration : null;
};

/**
 * Derive the leading edge of the interval a `CalculatedDatum` covers.
 *
 * `CalculatedDatum` carries only a trailing-edge `timestamp` — it has no start of its own,
 * and `CalculatedData.timeRange` is the envelope of the whole series rather than of one datum,
 * so it doesn't help either. What each datum does carry, between its own `timestamp` and the
 * container's `aggregation`, is the finish and the width of its interval, and that pair is
 * enough: the leading edge is the finish minus the width, computed below.
 *
 * The width is subtracted as a Luxon `Duration` in `timezone`: `P1D` is a calendar day, which
 * is 82800s on the day daylight saving starts and 90000s on the day it ends, while `PT30M` is
 * always 1800s. A `P1D` bucket is bounded by local midnights in the zone the server aggregated
 * in, so subtracting a fixed 86400s on a 23-hour day would place the derived start an hour
 * before the true bucket start, retaining a point that belongs to the next day.
 *
 * Any ISO 8601 duration with a positive width is accepted. A missing, malformed, zero or
 * negative `aggregation` returns `null` rather than guessing, so the point fails
 * `isValidTimeRange` downstream and is dropped instead of being placed at a wrong boundary.
 * @param {number} timestamp - Trailing edge of the interval, epoch seconds. Compared with `==
 *   null` rather than falsiness, because epoch-0 (1970-01-01T00:00:00Z) is a valid timestamp
 *   and `!0` would incorrectly treat it as absent.
 * @param {string} aggregation - ISO 8601 duration from the `CalculatedData` container.
 * @param {string} timezone - IANA zone the data was aggregated in. Required: without it a
 *   calendar-based interval such as `P1D` has no defined length.
 * @returns {number|null} Leading edge in epoch seconds, or null when `timestamp` or `timezone`
 *   is absent, `aggregation` is not a positive ISO 8601 duration, or the zone is invalid.
 */
export const getIntervalStart = (timestamp, aggregation, timezone) => {
  const duration = getAggregationDuration(aggregation);
  if (timestamp == null || !timezone || !duration) return null;

  const intervalStart = DateTime.fromSeconds(timestamp, { zone: timezone }).minus(duration);
  return intervalStart.isValid ? Math.floor(intervalStart.toSeconds()) : null;
};

/**
 * Whether a measurement's normalised range is well formed enough to be filtered.
 *
 * Unlike a rule, a measurement cannot carry an unbounded boundary: `hasTimeRangeOverlap`
 * reads a null boundary as unbounded, so a measurement with a missing boundary would be
 * treated as spanning all time and retained in every requested range. A trade entry from an
 * unmerged segment can carry `{ range: { start, finish: null } }`, and a historian point whose
 * container `aggregation` is unrecognised yields a null start, so both are screened out here.
 * @param {{ start: number|null, finish: number|null }} range - Normalised measurement range.
 * @returns {boolean} true when both boundaries are present.
 */
export const isValidTimeRange = ({ start, finish }) => start != null && finish != null;
